import { ObjectCustomField } from "@/backend/models/customFields/objectCustomField";
import { I18n } from "@/i18n/I18n";
import { ParameterInputType } from "@/types/parameterTypes";
import { Parameter } from "@/backend/models/parameter";
import { useState } from "react";
import { Form } from "react-bootstrap";
import { NonPrimitiveParameterModal } from "./NonPrimitiveParameterModal";
import { CUSTOM_FIELD_SUBJECT_TYPES, CUSTOM_FIELD_TYPES, ObjectKeyVisibility } from "@/types/customFieldTypes";
import { Pencil, Plus } from "lucide-react";
import { AddValueToParameterButtons } from "./AddValueToParameterButtons";
import { ObjectListCard } from "../objectList/ObjectListCard";
import { ListEdit } from "./ListEdit";
import { MultipleChoiceParameterSelect } from "./MultipleChoiceParameterSelect";
import { SingleChoiceParameterSelect } from "./SingleChoiceParameterSelect";
import { ButtonParent } from "../buttons/ButtonParent";
import { useParameterStore } from "@/frontend/store/ParameterStore";

export function NonPrimitiveParameter(props: { parameter: Parameter, runView: boolean, disabled?: boolean }) {
  const { parameter, runView, disabled } = props;

  const label = parameter.inputType === ParameterInputType.TABLE ? I18n.get('columns') : I18n.get('values');

  return (
    <>
      { !runView && <Form.Label className="col-form-label col-form-label-sm">{label}</Form.Label> }
      <ValueBody parameter={parameter} runView={runView} disabled={disabled} />
      { !disabled && <div className='mt-1'><EditButtons parameter={parameter} runView={runView} /></div> }
    </>
  );
}

function EditButtons(props: { parameter: Parameter, runView: boolean }) {
  const { parameter, runView } = props;
  const [showEditValues, setShowEditValues] = useState(false);
  const createCustomField = useParameterStore(state => state.createCustomField);

  if (parameter.fieldType === CUSTOM_FIELD_TYPES.Object) {
    if (parameter.inputType === ParameterInputType.FREE_FORM) {
      return null;
    }

    if (runView && parameter.isChoice() && parameter.propertyKeys.every(property => property.visibility !== ObjectKeyVisibility.EDITABLE && !parameter.allowCustomChoices)) {
      return null;
    }

    return (
      <>
        <button
          className="btn btn-primary fusa-btn-small-with-icon"
          type="button"
          onClick={() => setShowEditValues(true)}
          title={I18n.get('editModel', { model: I18n.get('parameter'), title: parameter.title })}
          aria-label={I18n.get('editModel', { model: I18n.get('parameter'), title: parameter.title })}
        >
          <Pencil size={16} />
          <span>{I18n.get('edit')}</span>
        </button>
        { showEditValues && <NonPrimitiveParameterModal parameterId={parameter.id} setShowEditValues={setShowEditValues} runView={runView}/>}
      </>
    );
  }

  if (runView && parameter.isChoice()) {
    if (parameter.allowCustomChoices) {
      const onAddValue = () => {
        void createCustomField({
          subjectId: parameter.id,
          subjectType: CUSTOM_FIELD_SUBJECT_TYPES.PARAMETER,
          fieldType: parameter.fieldType,
          isSelected: true,
          objectInstance:  {
            objectShapeId: parameter.objectShapeId,
            title: `${parameter.title} ${parameter.fields.length + 1}`,
          },
          isCustomChoice: true,
        });
      };

      return <ButtonParent className='fusa-btn-small-with-icon fusa-btn-primary' title={I18n.get('addCustomValue')} onClick={onAddValue}>
        <Plus size={16} />
        <span>{I18n.get('addCustomValue')}</span>
      </ButtonParent>;
    }

    return null;
  }

  return <AddValueToParameterButtons parameter={parameter} />;
}

function ValueBody(props: { parameter: Parameter, runView: boolean, disabled?: boolean }) {
  const { parameter, runView, disabled } = props;

  if (parameter.inputType === ParameterInputType.SINGLE_CHOICE && (runView || parameter.fieldType === CUSTOM_FIELD_TYPES.Object)) {
    return <SingleChoiceParameterSelect parameter={parameter} disabled={disabled} />;
  }

  if (parameter.inputType === ParameterInputType.MULTIPLE_CHOICE && (runView || parameter.fieldType === CUSTOM_FIELD_TYPES.Object)) {
    return <MultipleChoiceParameterSelect parameter={parameter} disabled={disabled} />;
  }

  if (parameter.fieldType !== CUSTOM_FIELD_TYPES.Object) {
    return <ListEdit parameter={parameter} disabled={disabled} />;
  }

  return (
    <div className='d-flex flex-column gap-1 my-auto'>{ parameter.fields.map(field =>
      <ObjectListCard
        key={field.id}
        item={field as ObjectCustomField}
        parameter={parameter}
        runView={runView}
        disabled={disabled}
      />
    )}</div>
  );
}
